import HomeIcon from '@ingka/ssr-icon/paths/home';
import ShoppingBagIcon from '@ingka/ssr-icon/paths/shopping-bag';
import GearIcon from '@ingka/ssr-icon/paths/gear';
import { UserRole } from '@pcmp-checka/lib-utilities/atkomst';
import { CombinationColor } from '@/redux/back-dashboard-api';
import KeyboardIcon from '@ingka/ssr-icon/paths/keyboard';
import ShieldIcon from '@ingka/ssr-icon/paths/shield-checkmark';
import TShirtIcon from '@ingka/ssr-icon/paths/t-shirt';
import IDIcon from '@ingka/ssr-icon/paths/id';
import FurnitureIcon from '@ingka/ssr-icon/paths/furniture-set';
import DrillIcon from '@ingka/ssr-icon/paths/drill';

export const SUPPORTED_LOCALES = [
  'es-ES',
  'sv-SE',
  'cs-CZ',
  'da-DK',
  'de-DE',
  'en-GB',
  'fi-FI',
  'fr-FR',
  'hr-HR',
  'hu-HU',
  'it-IT',
  'ja-JP',
  'ko-KR',
  'nl-NL',
  'no-NO',
  'pl-PL',
  'pt-PT',
  'ro-RO',
  'sk-SK',
  'sl-SI',
  'sr-RS',
  'uk-UA',
  'zh-CN',
];

export const QUERY_LIMIT = 9999;

export const DEFAULT_TOAST_DURATION_MS = 6000;

export const MOCK_USER = {
  email: 'katpurry.meowise@ingka.ikea.com',
  email_verified: true,
  family_name: 'Meowise',
  given_name: 'Kat Purry',
  name: 'Kat Purry Meowise',
  nickname: 'katpurry.meowise',
  picture: '/cat.png',
  sid: 'dAvR-oiPZ-hv_mya1aUSCB7EKAWuQkSq',
  sub: 'waad|DZkGZdj4KVz6BnAWiMq1HxjZF9ufWw-EVD2Az5xbEb0',
  updated_at: '2024-04-01T08:34:12.928Z',
};

export const MOCK_ROLES: UserRole[] = [
  {
    id: -1,
    clientReference: 'STO.006',
    name: 'CHECKA Location Admin',
    description: 'Allows to create orders.',
    service: 'CHECKA',
    permissions: [
      {
        name: 'order.create',
      },
    ],
  },
];

export const ROUTES: {
  translationKey: string;
  path: string;
  icon: any;
  isVisible: (opts: {
    isAdmin?: boolean;
    isInvitation?: boolean;
    isPlatformOwner?: boolean;
  }) => boolean;
  isActive: (currPath: string) => boolean;
}[] = [
  {
    translationKey: 'routes.myItemsAndOrders',
    path: '/',
    icon: HomeIcon,
    isVisible: () => true,
    isActive(currPath: string) {
      return !ROUTES.slice(1).some((route) => route.isActive(currPath));
    },
  },
  {
    translationKey: 'routes.shop',
    path: '/shop',
    icon: ShoppingBagIcon,
    isVisible: ({ isInvitation }) => !isInvitation,
    isActive(currPath: string) {
      return currPath.startsWith('/shop');
    },
  },
  {
    translationKey: 'routes.admin',
    path: '/admin',
    icon: GearIcon,
    isVisible: ({ isAdmin }) => !!isAdmin,
    isActive(currPath: string) {
      return currPath.startsWith('/admin');
    },
  },
  {
    translationKey: 'routes.superadmin',
    path: '/superadmin',
    icon: ShieldIcon,
    isVisible: ({ isPlatformOwner }) => !!isPlatformOwner,
    isActive(currPath: string) {
      return currPath.startsWith('/superadmin');
    },
  },
];

export const COLORS: { value: CombinationColor; i18nKey: string }[] = [
  {
    value: CombinationColor.Black,
    i18nKey: 'label.colorNames.black',
  },
  {
    value: CombinationColor.Blue,
    i18nKey: 'label.colorNames.blue',
  },
  {
    value: CombinationColor.Green,
    i18nKey: 'label.colorNames.green',
  },
  {
    value: CombinationColor.Grey,
    i18nKey: 'label.colorNames.grey',
  },
  {
    value: CombinationColor.Orange,
    i18nKey: 'label.colorNames.orange',
  },
  {
    value: CombinationColor.Red,
    i18nKey: 'label.colorNames.red',
  },
  {
    value: CombinationColor.Yellow,
    i18nKey: 'label.colorNames.yellow',
  },
  {
    value: CombinationColor.White,
    i18nKey: 'label.colorNames.white',
  },
  {
    value: CombinationColor.Multicolor,
    i18nKey: 'label.colorNames.multicolor',
  },
  {
    value: CombinationColor.Striped,
    i18nKey: 'label.colorNames.striped',
  },
  {
    value: CombinationColor.Unknown,
    i18nKey: 'label.colorNames.unknown',
  },
];

export const FITS = [
  // Slim Fit: Tapered; hugs the body
  {
    value: 'Slim',
    i18nKey: 'label.fitNames.slim',
  },
  //Regular Fit: Standard cut; not too tight or loose
  {
    value: 'Regular',
    i18nKey: 'label.fitNames.regular',
  },
  // Relaxed Fit: Looser than regular; more room in the chest and sleeves
  {
    value: 'Relaxed',
    i18nKey: 'label.fitNames.relaxed',
  },
  // Oversized Fit: Intentionally large; dropped shoulders and wide silhouette
  {
    value: 'Oversized',
    i18nKey: 'label.fitNames.oversized',
  },
  // Boxy Fit: Wide and short; often with a straight hem
  {
    value: 'Boxy',
    i18nKey: 'label.fitNames.boxy',
  },
  // Skinny Fit: Very tight from waist to ankle
  {
    value: 'Skinny',
    i18nKey: 'label.fitNames.skinny',
  },
  // Straight Fit: Same width from thigh to ankle
  {
    value: 'Straight',
    i18nKey: 'label.fitNames.straight',
  },
  // Loose Fit: Very spacious; often used in streetwear
  {
    value: 'Loose',
    i18nKey: 'label.fitNames.loose',
  },
  // Tapered Fit: Wider at the top, narrows toward the ankle
  {
    value: 'Tapered',
    i18nKey: 'label.fitNames.tapered',
  },
  // Wide Fit: Loose throughout, especially below the knee
  {
    value: 'Wide',
    i18nKey: 'label.fitNames.wide',
  },
  // Unisex Fit: No distinction
  {
    value: 'Unisex',
    i18nKey: 'label.fitNames.unisex',
  },
];

export const CATEGORIES_ICONS: Record<number, any> = {
  // Personal Safety and Protection
  0: ShieldIcon,
  // Clothing
  100: TShirtIcon,
  // Computer Equipment and Accessories
  200: KeyboardIcon,
  // Signage and accessories
  300: IDIcon,
  // Furniture and Furnishings
  400: FurnitureIcon,
  // Tools
  500: DrillIcon,
};
